import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ShieldCheck, ArrowUpRight, Loader2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const TIERS = [
    {
        name: "The Signal",
        tagline: "Know your light.",
        gbp: "£249",
        usd: "$329",
        gbpAfter: "£349",
        usdAfter: "$459",
        cadence: "one-off",
        service: "The Signal",
        highlight: false,
        launch: true,
        pkg: "signal",
        features: [
            "90-min 1:1 diagnosis call",
            "Full Red / Amber / Green verdict",
            "Your hidden money-leak report",
            "Your 3 highest-impact moves",
        ],
    },
    {
        name: "The Dream Plan",
        tagline: "Build the roadmap to it.",
        gbp: "£749",
        usd: "$989",
        gbpAfter: "£999",
        usdAfter: "$1,319",
        cadence: "one-off",
        service: "The Dream Plan",
        highlight: true,
        badge: "Most chosen",
        launch: true,
        pkg: "dream",
        features: [
            "Everything in The Signal",
            "Custom roadmap for your goal",
            "The real date you'll hit it",
            "Acceleration strategy, step by step",
            "2 follow-up calls",
        ],
    },
    {
        name: "The Business Edge",
        tagline: "Math for your bottom line.",
        gbp: "Get in touch",
        usd: "Get in touch",
        cadence: "custom",
        service: "The Business Edge",
        highlight: false,
        contactOnly: true,
        features: [
            "Cost & cash-flow analysis",
            "Waste & leak identification",
            "Decision-ready reporting",
            "Ongoing retainer options",
        ],
    },
];

export const Pricing = () => {
    const [currency, setCurrency] = useState("gbp");
    const [loadingPkg, setLoadingPkg] = useState(null);

    const startCheckout = async (tier) => {
        // Business Edge (no package) -> go to the message form for a quote
        if (!tier.pkg) {
            if (!scrollToId("#contact")) window.location.href = "/#contact";
            return;
        }
        setLoadingPkg(tier.pkg);
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                lookup_key: `${tier.pkg}_${currency}`,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error("Couldn't start checkout. Please try again.");
            setLoadingPkg(null);
        }
    };

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32"
        >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                <div className="max-w-2xl">
                    <p className="overline text-[#C5A059] mb-6">
                        — Straight pricing, no surprises
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-tight">
                        Cheaper than{" "}
                        <span className="italic text-[#C5A059]">
                            staying stuck
                        </span>
                        .
                    </h2>
                </div>

                {/* Currency toggle UK / US */}
                <div
                    data-testid="currency-toggle"
                    className="inline-flex items-center border border-white/15 rounded-full p-1 self-start"
                >
                    <button
                        data-testid="currency-gbp"
                        onClick={() => setCurrency("gbp")}
                        className={`overline px-5 py-2.5 rounded-full transition-colors duration-300 ${
                            currency === "gbp"
                                ? "bg-[#C5A059] text-[#070A13]"
                                : "text-white/50"
                        }`}
                    >
                        £ UK
                    </button>
                    <button
                        data-testid="currency-usd"
                        onClick={() => setCurrency("usd")}
                        className={`overline px-5 py-2.5 rounded-full transition-colors duration-300 ${
                            currency === "usd"
                                ? "bg-[#C5A059] text-[#070A13]"
                                : "text-white/50"
                        }`}
                    >
                        $ US
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                {TIERS.map((t, i) => (
                    <motion.div
                        key={t.name}
                        data-testid={`tier-${t.service.toLowerCase().replace(/\s/g, "-")}`}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: i * 0.1, ease }}
                        className={`relative flex flex-col p-8 md:p-10 border transition-transform duration-300 hover:-translate-y-1 ${
                            t.highlight
                                ? "bg-[#11141D] text-[#FDFBF7] border-[#C5A059]"
                                : "bg-[#11141D] text-[#FDFBF7] border-white/10"
                        }`}
                    >
                        {t.badge && (
                            <span className="absolute -top-3 left-8 overline bg-[#C5A059] text-[#070A13] px-4 py-1.5">
                                {t.badge}
                            </span>
                        )}
                        <p
                            className={`overline mb-2 ${t.highlight ? "text-[#C5A059]" : "text-white/40"}`}
                        >
                            {t.tagline}
                        </p>
                        <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-6">
                            {t.name}
                        </h3>
                        <div className="flex items-baseline gap-2 mb-3">
                            <span
                                className={`font-display tracking-tighter ${t.contactOnly ? "text-3xl md:text-4xl" : "text-5xl"}`}
                            >
                                {t[currency]}
                            </span>
                            {!t.contactOnly && (
                                <span
                                    className={`font-mono text-xs uppercase tracking-wider ${t.highlight ? "text-white/50" : "text-white/40"}`}
                                >
                                    / {t.cadence}
                                </span>
                            )}
                        </div>
                        {t.launch ? (
                            <p className="font-mono text-xs text-[#C5A059] mb-8 flex items-center gap-2 flex-wrap">
                                <span className="inline-block w-2 h-2 rounded-full bg-[#C5A059] animate-pulse" />
                                First 10 calls only — then{" "}
                                {currency === "gbp" ? t.gbpAfter : t.usdAfter}
                            </p>
                        ) : (
                            <div className="mb-8" />
                        )}
                        <ul className="flex flex-col gap-3.5 mb-10">
                            {t.features.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-3 font-body"
                                >
                                    <Check
                                        size={18}
                                        className="mt-0.5 shrink-0 text-[#C5A059]"
                                    />
                                    <span
                                        className={
                                            t.highlight
                                                ? "text-white/85"
                                                : "text-[#8E95A3]"
                                        }
                                    >
                                        {f}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <button
                            data-testid={`tier-cta-${t.service.toLowerCase().replace(/\s/g, "-")}`}
                            onClick={() => startCheckout(t)}
                            disabled={loadingPkg === t.pkg}
                            className={`group mt-auto inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-body font-semibold transition-colors duration-300 disabled:opacity-70 ${
                                t.highlight
                                    ? "bg-[#C5A059] text-[#070A13] hover:bg-[#11141D] hover:text-[#FDFBF7]"
                                    : "bg-[#C5A059] text-[#070A13] hover:bg-[#C5A059]"
                            }`}
                        >
                            {loadingPkg === t.pkg ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Redirecting…
                                </>
                            ) : (
                                <>
                                    {t.cadence === "custom"
                                        ? "Contact me"
                                        : "Pay & book"}
                                    <ArrowUpRight
                                        size={18}
                                        className="transition-transform duration-300 group-hover:rotate-45"
                                    />
                                </>
                            )}
                        </button>
                    </motion.div>
                ))}
            </div>

            {/* Scarcity */}
            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-center"
            >
                <p className="flex items-center gap-2 font-body text-[#8E95A3]">
                    <ShieldCheck size={18} className="text-[#C5A059]" />
                    Pay first, then book your slot — instant confirmation.
                </p>
                <p className="font-mono text-xs tracking-[0.15em] uppercase text-[#C5A059]">
                    Only 8 diagnosis calls open each month
                </p>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-6 font-mono text-[10px] tracking-[0.15em] uppercase text-white/30 text-center"
            >
                No refunds · once booked, a call counts as used · no-shows are not rescheduled free
            </motion.p>
        </section>
    );
};
